import { useState, useEffect } from 'react'
import { TabDisplay } from '../../../Components/TabDisplay'
import { Box, Breadcrumbs, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material'
import { ChildDetails } from './ChildDetails'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { sendGet } from '../../../hooks/use-fetch'
import { CommunicationList } from './CommunicationList'
import { CareNetworkServices } from './CareNetworkServices'
import { type PersonBasicInfo, AccountType } from '../../../core/types'
import { ErrorProvider } from '../../../ContextProviders/ErrorProvider'
import { usePermissions } from '../../../hooks/use-permissions'
import { FIND_CHILDREN, CARE_MANAGEMENT } from '../../Routes'
import { useAccountId } from '../../../ContextProviders/CurrentAccount'
import { ChildHistoryDisplay } from './ChildHistoryDisplay'
import { ChildAndGuardianDocumentList } from '../../../Components/DocumentList/ChildAndGuardianDocumentList'
import { SupplementalNutritionList } from './SupplementalNutritionList'
import { useRefresh } from '../../../ContextProviders/RefreshProvider'
import { useAuth } from '../../../hooks/use-auth'

export function CareNetworkDetail (): JSX.Element {
  const nav = useNavigate()
  const perms = usePermissions()
  const auth = useAuth()
  const [params] = useSearchParams()
  const [loaded, setLoaded] = useState(false)
  const [child, setChild] = useState<PersonBasicInfo>()
  const [tab, setTab] = useState(Number(params.get('tab') ?? '0'))
  const { id } = useParams()
  const account = useAccountId()
  const accountid = account?.id ?? 0
  const isdid: number = account?.type === 'Intake'
    ? auth?.user?.currentISDID ?? child?.isdId ?? 0
    : (account?.type === AccountType.SERVICE_PROVIDER
        ? (auth.user?.currentISDID ?? child?.isdId ?? accountid)
        : accountid)
  const refreshTime = useRefresh()
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (id !== undefined && accountid !== 0) {
        const isdString = account?.type === AccountType.ISD ? `&isdid=${accountid}` : ''
        const { response: details } = await sendGet(`/ChildDetails/BasicInfo?id=${id}${isdString}`, {})
        if (details !== undefined) {
          setChild(details.response)
        }
        setLoaded(true)
      }
    }

    void fetchData()
  }, [id, accountid, refreshTime])

  if (!loaded) return <CircularProgress role='loading' />
  return <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="h3" data-testid="page-header">Details - {child?.firstName} {child?.lastName}</Typography>
    </Box>
    <Breadcrumbs maxItems={1} aria-label="breadcrumb">
      {params.get('from') == null || params.get('from') === 'cn'
        ? (
          <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={() => { nav(FIND_CHILDREN) }} >
            Return to Find Children
          </Link>)
        : (
          <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={() => { nav(CARE_MANAGEMENT) }} >
            Return to Care Management
          </Link>)
      }
    </Breadcrumbs>

    <Tabs value={tab}
      data-testid='tabs'
      onChange={(e, val) => { setTab(val) }}
      aria-label="Care Network Detail Tabs"
      textColor="secondary"
      indicatorColor="secondary"
    >
      <Tab label="Details" value={0} data-testid="details" />
      <Tab label="Documents" value={1} data-testid="documents" />
      {perms.CareNetwork_Enrollment &&
        <Tab label="Services" value={2} data-testid="services" />
      }
      {perms.CareNetwork_Communication && (
        <Tab label="Communication" value={3} data-testid="communication" />
      )}
      {perms.CareNetwork_SupplementalNutrition && (
        <Tab label='Supplemental Nutrition' value={4} data-testid="supplemental-nutrition" />
      )}
      <Tab label="History" value={5} data-testid="history" />
    </Tabs>
    <TabDisplay
      index={0}
      value={tab}
    >
      <ChildDetails childID={Number(id)} />
    </TabDisplay>
    <TabDisplay
      index={1}
      value={tab}
    >
      <Box className='CardDisplay' sx={{ maxWidth: '1000px', mt: 2, mx: { xs: 0, sm: 4, lg: 6 } }}>
        <ChildAndGuardianDocumentList
          childID={id !== undefined ? parseInt(id) : 0}
          editable={perms.CareNetwork_Documentation_Edit ?? false}
          isdID={isdid}
        />
      </Box>
    </TabDisplay>
    <TabDisplay
      index={2}
      value={tab}
    >
      <CareNetworkServices isdId={isdid} childName={`${child?.firstName ?? ''} ${child?.lastName ?? ''}`}
        childID={id !== undefined ? parseInt(id) : 0}
        districtId={child?.districtId ?? 0}
        readonly={!perms.CareNetwork_Enrollment_Edit}
      />
    </TabDisplay>
    <TabDisplay
      index={3}
      value={tab}
    >
      <ErrorProvider>
        <CommunicationList childId={Number(id)} />
      </ErrorProvider>
    </TabDisplay>

    <TabDisplay
      index={4}
      value={tab}
    >
      <SupplementalNutritionList childId={Number(id)} />
    </TabDisplay>

    <TabDisplay
      index={5}
      value={tab}
    >
      <ChildHistoryDisplay childId={Number(id)} accountId={isdid} />
    </TabDisplay>
  </Box>
}
